import { ImageIcon, SpinnerGapIcon, XIcon } from '@phosphor-icons/react';
import Tooltip from '@/components/ui/Tooltip';
import { useImageDecodeFallback } from 'src/lib/useImageDecodeFallback';

export interface ImageItemPillProps {
  src: string;
  alt: string;
  /**
   * A browser-renderable image tried only when `src` fails to decode. Lets a
   * HEIC-capable browser (Safari) show the original while others fall back to a
   * derived JPEG. Support is detected by the decode itself (EAFP), so a browser
   * that later gains HEIC support renders the original with no code change.
   */
  fallbackSrc?: string;
  /** Upload status, or undefined for completed images from history. */
  status?: 'uploading' | 'scanning' | 'processing' | 'complete';
  /** Upload progress percentage (0–100). Only meaningful when status is 'uploading'. */
  uploadProgress?: number;
  /** Context percent label. Shown when exceedsContextLimit is true. */
  contextPercent?: number | null;
  exceedsContextLimit?: boolean;
  /** Called when the image is clicked (e.g. to open in modal). */
  onImageClick?: () => void;
  /** Called when the remove button is clicked. If not provided, no remove button is shown. */
  onRemove?: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  uploading: 'Uploading…',
  scanning: 'Scanning…',
  processing: 'Processing…'
};

export default function ImageItemPill({
  src,
  alt,
  fallbackSrc,
  status,
  uploadProgress = 0,
  contextPercent,
  exceedsContextLimit,
  onImageClick,
  onRemove
}: ImageItemPillProps) {
  const isUploading = status === 'uploading';
  const isActive = status === 'uploading' || status === 'scanning' || status === 'processing';
  const label = status ? STATUS_LABEL[status] : undefined;

  // The `fallbackSrc` (rendition SAS) is only known once the upload registers, so
  // it can arrive after the decode failure; the hook keeps the failure sticky per
  // `src` so it still swaps in. Until something can paint (a HEIC whose rendition
  // does not exist yet), a neutral tile stands in for the broken-image glyph.
  const { effectiveSrc, canPaint, onError, onLoadCheck } = useImageDecodeFallback(src, fallbackSrc);

  return (
    <div className="relative group">
      {canPaint ? (
        <img
          src={effectiveSrc}
          alt={alt}
          className={`max-w-[200px] max-h-[100px] object-contain rounded-lg transition-opacity ${
            isActive ? 'opacity-60' : 'cursor-pointer hover:opacity-80'
          }`}
          onClick={!isActive ? onImageClick : undefined}
          onError={onError}
          onLoad={(e) => {
            onLoadCheck(e.currentTarget);
          }}
        />
      ) : (
        <div className="flex h-[90px] w-[130px] items-center justify-center rounded-lg bg-gray-100" title={alt}>
          <ImageIcon size={28} weight="light" className="text-gray-300" />
        </div>
      )}
      {isActive && (
        <div className="absolute inset-0 rounded-lg flex flex-col items-center justify-center gap-1.5 pointer-events-none">
          {isUploading ? (
            <>
              <div className="w-3/4 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-400 rounded-full transition-[width] duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="text-[10px] font-medium text-gray-600 bg-white/80 px-1.5 py-0.5 rounded">
                {uploadProgress < 100 ? `${uploadProgress}%` : 'Uploading…'}
              </span>
            </>
          ) : (
            <>
              <SpinnerGapIcon size={18} className="animate-spin text-gray-600" />
              {label && (
                <span className="text-[10px] font-medium text-gray-600 bg-white/80 px-1.5 py-0.5 rounded">{label}</span>
              )}
            </>
          )}
        </div>
      )}
      {exceedsContextLimit && contextPercent != null && (
        <Tooltip
          title={
            contextPercent > 100
              ? `This file uses ${contextPercent}% of CompliMind chat context limit and is too large to process on its own.`
              : `This file uses ${contextPercent}% of CompliMind chat context limit.`
          }
          enterDelay={0}
          leaveDelay={0}
        >
          <span
            className={`absolute bottom-1 left-1 text-xs px-1 rounded bg-white/80 ${contextPercent > 100 ? 'text-red-500' : 'text-gray-500'}`}
          >
            {contextPercent}%
          </span>
        </Tooltip>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute -top-1 -right-1 bg-gray-100 rounded-tr rounded-bl p-0.5 text-gray-500 hover:text-gray-700 hover:bg-gray-50"
        >
          <XIcon size={14} />
        </button>
      )}
    </div>
  );
}
